import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    FaTachometerAlt,
    FaUsers,
    FaQrcode,
    FaUserPlus,
    FaPoll,
    FaCog,
    FaChartBar,
    FaSignOutAlt,
} from "react-icons/fa";
import { UserButton, useUser, useClerk } from "@clerk/clerk-react";
import { LogOut, AlignJustify, Menu } from "lucide-react";
import BottomBar from "../Components/BottomBar";

const AdminDashboard = () => {
    const [activePage, setActivePage] = useState("dashboard");
    const location = useLocation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const { user } = useUser();
    const clerk = useClerk();

    //Sidebar states
    const [isExpanded, setIsExpanded] = useState(window.innerWidth > 768);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    //CP
    const colors = {
        primary: "#154c79",
        secondary: "#0a2640",
        accent: "#2d7fc1",
        light: "#e9f2fb",
        dark: "#0a1622",
        white: "#ffffff",
    };

    const toggleSidebar = () => {
        if (isMobile) {
            setMobileMenuOpen(!mobileMenuOpen);
        } else {
            setIsExpanded(!isExpanded);
        }
    };

    useEffect(() => {
        const handleResize = () => {
            const isSmallScreen = window.innerWidth <= 768;
            setIsMobile(isSmallScreen);

            if (isSmallScreen) {
                setIsExpanded(false);
                setMobileMenuOpen(false);
            } else {
                setIsExpanded(true);
            }
        };

        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location]);

    const isActive = (path) => location.pathname === path;

    const handleLogout = async () => {
        try {
            await clerk.signOut();
            console.log("Successfully signed out");
            window.location.href = "/LandingPage";
        } catch (error) {
            console.error("Error during signOut:", error);
            window.location.href = "/LandingPage";
        }
    };

    const renderContent = () => {
        switch (activePage) {
            case "dashboard":
                return (
                    <div className="p-4 md:p-6">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                            <div className="bg-white p-4 md:p-6 rounded shadow text-center">
                                <h3 className="text-gray-600 text-base md:text-lg">Total Elections</h3>
                                <p className="text-xl md:text-2xl font-bold">5</p>
                            </div>
                            <div className="bg-white p-4 md:p-6 rounded shadow text-center">
                                <h3 className="text-gray-600 text-base md:text-lg">Registered Voters</h3>
                                <p className="text-xl md:text-2xl font-bold">2,450</p>
                            </div>
                            <div className="bg-white p-4 md:p-6 rounded shadow text-center">
                                <h3 className="text-gray-600 text-base md:text-lg">Total Candidates</h3>
                                <p className="text-xl md:text-2xl font-bold">35</p>
                            </div>
                            <div className="bg-white p-4 md:p-6 rounded shadow text-center">
                                <h3 className="text-gray-600 text-base md:text-lg">Voter Turnout</h3>
                                <p className="text-xl md:text-2xl font-bold text-blue-600">78%</p>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="mt-4 md:mt-6 bg-white p-4 md:p-6 rounded shadow">
                            <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">Recent Activity</h3>
                            <ul className="text-gray-700 space-y-2">
                                <li>
                                    New election created: <strong>Municipal Election 2025</strong>
                                </li>
                                <li>5 new candidates registered</li>
                                <li>Voter verification process initiated</li>
                                <li>Statistics updated for Regional Election</li>
                            </ul>
                        </div>
                    </div>
                );
            case "ballot-sheet":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Ballot Sheet Page</div>;
            case "candidates":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Manage Candidates</div>;
            case "company-profile":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Company Profile</div>;
            case "qrcode-scan":
                return <div className="p-4 md:p-6 bg-white rounded shadow">QR Code Scanning</div>;
            case "register-candidate":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Register Candidate</div>;
            case "register-election":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Election Registration</div>;
            case "settings":
                return <div className="p-4 md:p-6 bg-white rounded shadow">System Settings</div>;
            case "voter-registration":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Voter Registration</div>;
            case "voters":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Voter Management</div>;
            case "voting-statistics":
                return <div className="p-4 md:p-6 bg-white rounded shadow">Voting Statistics</div>;
            default:
                return null;
        }
    };

    // Navigation Links for both desktop and mobile
    const NavigationLinks = () => (
        <>
            <div className={'mt-5'}>


            <Link
                to="/RegisterElectionPage"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/RegisterElectionPage")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/RegisterElectionPage")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/RegisterElectionPage")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("register-election")}
            >
                {!isExpanded && !isMobile ? (
                    <FaPoll className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaPoll className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Register Election
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/RegisterCandidatePage"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/RegisterCandidatePage")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/RegisterCandidatePage")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/RegisterCandidatePage")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("register-candidate")}
            >
                {!isExpanded && !isMobile ? (
                    <FaUserPlus className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaUserPlus className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Register Candidate
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/VoterRegistration"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/VoterRegistration")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/VoterRegistration")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/VoterRegistration")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("voter-registration")}
            >
                {!isExpanded && !isMobile ? (
                    <FaUserPlus className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaUserPlus className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Register Voter
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/CandidatesPage"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/CandidatesPage")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/CandidatesPage")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/CandidatesPage")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("candidates")}
            >
                {!isExpanded && !isMobile ? (
                    <FaUsers className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaUsers className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Candidates List
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/Voters"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/Voters")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/Voters")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/Voters")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("voters")}
            >
                {!isExpanded && !isMobile ? (
                    <FaUsers className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaUsers className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Voters List
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/VotingStatisticsUpdated"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/VotingStatisticsUpdated")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/VotingStatisticsUpdated")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/VotingStatisticsUpdated")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("voting-statistics")}
            >
                {!isExpanded && !isMobile ? (
                    <FaChartBar className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaChartBar className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            Voting Statistics
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>

            <Link
                to="/QRScanPage"
                className={`flex items-center px-4 py-3 w-full rounded transition-all duration-200 relative ${
                    isActive("/QRScanPage")
                        ? "bg-opacity-90"
                        : "hover:bg-opacity-30 hover:translate-x-1"
                }`}
                style={{
                    backgroundColor: isActive("/QRScanPage")
                        ? colors.primary
                        : "transparent",
                    borderRight: isActive("/QRScanPage")
                        ? `4px solid ${colors.accent}`
                        : "none",
                }}
                onClick={() => setActivePage("qrcode-scan")}
            >
                {!isExpanded && !isMobile ? (
                    <FaQrcode className="mx-auto text-white w-5 h-5" />
                ) : (
                    <>
                        <FaQrcode className="mr-3 text-white w-5 h-5 opacity-70" />
                        <span className="relative font-medium group">
                            QR Code Scan
                            <span
                                className="absolute bottom-0 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                                style={{ backgroundColor: colors.accent }}
                            ></span>
                        </span>
                    </>
                )}
            </Link>
            </div>
        </>
    );

    return (
        <div className="flex flex-col md:flex-row h-screen bg-bgColor font-[Montserrat]">
            {isMobile && mobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    onClick={() => setMobileMenuOpen(false)}
                ></div>
            )}

            <aside
                className={`${
                    isMobile
                        ? `fixed inset-y-0 left-0 z-50 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} w-64`
                        : isExpanded ? 'w-64' : 'w-16'
                } bg-customeBlue text-white transition-all duration-300 ease-in-out flex-shrink-0`}
                style={{
                    backgroundColor: colors.dark,
                    boxShadow: "0 0 15px rgba(0,0,0,0.2)",
                }}
            >
                <div className="flex flex-col h-full">
                    {/* Sidebar Header */}
                    <div className="flex items-center justify-between px-5 py-5">
                        {(isExpanded || isMobile) && <h2 className="text-xl font-bold text-white">Admin</h2>}
                        <AlignJustify
                            onClick={toggleSidebar}
                            className="text-white cursor-pointer hover:scale-110 transition-transform duration-200"
                        />
                    </div>

                    {/* Navigation */}
                    <nav className="space-y-1 px-2 py-2 overflow-y-auto">
                        <NavigationLinks />
                    </nav>

                    {/* Bottom Section with Logout */}
                    <div className="mt-auto flex flex-col items-center mb-5 space-y-4">
                        {(isExpanded || isMobile) && (
                            <div
                                className="w-40 h-0.5 shadow-md"
                                style={{ backgroundColor: colors.accent }}
                            ></div>
                        )}
                        <LogOut
                            onClick={handleLogout}
                            className="text-white w-6 h-6 cursor-pointer transition-all duration-300 hover:text-red-300 hover:scale-110"
                        />
                    </div>
                </div>
            </aside>

            <div className="flex-1 flex flex-col h-screen overflow-hidden">
                
                <header className="bg-[#0a1622] p-3 md:p-4 flex justify-between items-center shadow">
                    
                    {isMobile && (
                        <button
                            onClick={toggleSidebar}
                            className="text-white"
                        >
                            <Menu size={24} />
                        </button>
                    )}

            
                    <div className="flex-1 text-center">
                        <h1 className="text-white text-lg md:text-xl font-semibold">Dashboard</h1>
                    </div>


                    <div className="relative flex items-center">
                        <div className="hidden sm:flex items-center gap-x-4 text-white mr-5">
                            {user?.firstName}
                        </div>
                        <UserButton />
                        {dropdownOpen && (
                            <div className="absolute right-0 mt-2 w-40 bg-white shadow-md rounded-lg overflow-hidden z-50 top-full">
                                <button
                                    onClick={handleLogout}
                                    className="flex items-center px-4 py-2 text-red-600 hover:bg-gray-100 w-full"
                                >
                                    <FaSignOutAlt className="mr-2" /> Logout
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto">
                    {renderContent()}
                </main>

                <footer className="bg-customeBlue">
                    <BottomBar />
                </footer>
            </div>
        </div>
    );
};

export default AdminDashboard;